<template>
  <d-modal
    class="modal-big"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('complaints.popup.addComplaint') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form>
        <validation-observer ref="complaint-add-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('complaints.popup.description') }}:
            </label>

            <d-textarea
              :value.prop="description"
              v-model="description" />
          </div>

          <div class="form-group">
            <label>
              {{ $t('complaints.popup.handler') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="workers"
                  v-model="handled_by_id"
                  :reduce="worker => worker.id"
                  :filterBy="filterByForWorkers"
                  :placeholder="$t('users.selectWorker')"
                  label="name"
                  :clearable="false">
                  <template slot="no-options">
                    {{ $t('users.noUsersMatchingPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>

              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>

            <div class="form-group">
              <h6 class="mt-4 mb-2">
                {{ $t('complaints.popup.complainedProducts') }}:
              </h6>

              <div
                v-for="(product, index) of complainedProducts"
                :key="'product-item-' + index"
                class="product-item">
                <div class="product-item-wrapper d-flex">
                  <d-checkbox
                    inline
                    v-model="product.is_complained"
                    toggle
                    toggle-small>
                  </d-checkbox>
                  <strong>
                    {{ product.order_name }}
                  </strong>
                </div>

                <div
                  v-if="product.is_complained"
                  class="product-item-complaint-params mt-2 mb-4">
                  <div class="d-flex">
                    <d-textarea
                      :value.prop="product.complaint_description"
                      v-model="product.complaint_description"
                      class="w-50"
                      :placeholder="$t('complaints.popup.enterComplaintDescription')" />
                    <validation-provider
                      :rules="'required|min_value:1|max_value:' + product.order_quantity"
                      :name="'product-item-complaint-quantity' + index"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-input-group
                        :class="{
                          'is-invalid': errors.length
                        }">
                        <input
                          :class="{
                            'form-control': true,
                            'is-invalid': errors.length
                          }"
                          :invalid="errors.length ? true : null"
                          type="number"
                          v-model="product.complaint_quantity" />
                        <d-input-group-text slot="append">
                          {{ $t('ui.piecesAbbr') }}
                        </d-input-group-text>
                      </d-input-group>
                      <d-form-invalid-feedback>
                        {{ errors[0] }}
                      </d-form-invalid-feedback>
                    </validation-provider>
                    <validation-provider
                      rules="required"
                      :name="'product-item-complaint-status' + index"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="product.complaint_status"
                        :state="errors.length ? 'invalid' : null"
                        :options="complaintStatusOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>

                <div
                  v-for="(service, subindex) of product.services"
                  :key="'product-item-' + index + '-service-' + subindex"
                  class="product-item-service-item">

                  <div class="product-item-service-item-wrapper d-flex ml-5">
                    <d-checkbox
                      inline
                      v-model="service.is_complained"
                      toggle
                      toggle-small>
                    </d-checkbox>
                    <strong>
                      {{ service.order_name }}
                    </strong>
                  </div>

                  <div
                    v-if="service.is_complained"
                    class="product-item-service-item-complaint-params d-flex ml-5 mt-2 mb-4">
                    <d-textarea
                      :value.prop="service.complaint_description"
                      v-model="service.complaint_description"
                      class="w-50"
                      :placeholder="$t('complaints.popup.enterComplaintDescription')" />
                    <validation-provider
                      :rules="'required|min_value:1|max_value:' + service.order_quantity"
                      :name="'product-item-' + index + '-complaint-quantity-' + subindex"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-input-group
                        :class="{
                          'is-invalid': errors.length
                        }">
                        <input
                          :class="{
                            'form-control': true,
                            'is-invalid': errors.length
                          }"
                          :invalid="errors.length ? true : null"
                          type="number"
                          v-model="service.complaint_quantity" />
                        <d-input-group-text slot="append">
                          {{ $t('ui.piecesAbbr') }}
                        </d-input-group-text>
                      </d-input-group>
                      <d-form-invalid-feedback>
                        {{ errors[0] }}
                      </d-form-invalid-feedback>
                    </validation-provider>
                    <validation-provider
                      rules="required"
                      :name="'product-item-' + index + '-complaint-status-' + subindex"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="service.complaint_status"
                        :state="errors.length ? 'invalid' : null"
                        :options="complaintStatusOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </div>

              <h6
                v-if="complainedServices.length"
                class="mt-4 mb-2">
                {{ $t('complaints.popup.complainedServices') }}:
              </h6>

              <div
                v-for="(service, index) of complainedServices"
                :key="'service-item-' + index"
                class="service-item">
                <div class="service-item-wrapper d-flex">
                  <d-checkbox
                    inline
                    v-model="service.is_complained"
                    toggle
                    toggle-small>
                  </d-checkbox>
                  <strong>
                    {{ service.order_name }}
                  </strong>
                </div>

                <div
                  v-if="service.is_complained"
                  class="service-item-complaint-params mt-2 mb-4">
                  <div class="d-flex">
                    <d-textarea
                      :value.prop="service.complaint_description"
                      v-model="service.complaint_description"
                      class="w-50"
                      :placeholder="$t('complaints.popup.enterComplaintDescription')" />
                    <validation-provider
                      :rules="'required|min_value:1|max_value:' + service.order_quantity"
                      :name="'product-item-complaint-quantity' + index"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-input-group
                        :class="{
                          'is-invalid': errors.length
                        }">
                        <input
                          :class="{
                            'form-control': true,
                            'is-invalid': errors.length
                          }"
                          :invalid="errors.length ? true : null"
                          type="number"
                          v-model="service.complaint_quantity" />
                        <d-input-group-text slot="append">
                          {{ $t('ui.piecesAbbr') }}
                        </d-input-group-text>
                      </d-input-group>
                      <d-form-invalid-feedback>
                        {{ errors[0] }}
                      </d-form-invalid-feedback>
                    </validation-provider>
                    <validation-provider
                      rules="required"
                      :name="'service-item-complaint-status' + index"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="service.complaint_status"
                        :state="errors.length ? 'invalid' : null"
                        :options="complaintStatusOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </div>

              <h6 class="mt-4 mb-2">
                {{ $t('complaints.popup.complainTransport') }}:
              </h6>

              <div class="d-flex w-100 mt-2">
                <d-checkbox
                  inline
                  v-model="transportIsComplained"
                  toggle
                  toggle-small
                  class="mt-1">
                </d-checkbox>

                <strong class="pt-1" style="white-space: nowrap;">
                  {{ $t('complaints.popup.complainTransport') }}
                </strong>

                <validation-provider
                  v-if="transportIsComplained"
                  rules="required"
                  class="ml-3 w-100"
                  name="tracking-number-is-complained"
                  v-slot="{ errors }">
                  <d-input-group
                    :class="{
                      'is-invalid': errors.length,
                      'w-100': true
                    }">
                    <d-input-group-text slot="prepend">
                      {{ $t('complaints.popup.trackingNumber') }}
                    </d-input-group-text>
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="text"
                      v-model="shipment_tracking_number" />
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>
            </div>
          </div>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('complaints.popup.addComplaint') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'complaint-new-popup',
  props: {
    orderID: {
      default: 0,
      type: Number
    },
    products: {
      default: () => ([]),
      type: Array
    },
    services: {
      default: () => ([]),
      type: Array
    }
  },
  computed: {
    complaintStatusOptions () {
      return [{
        text: this.$t('complaints.statusNames.received'),
        value: 'RECEIVED'
      }, {
        text: this.$t('complaints.statusNames.negotiated'),
        value: 'NEGOTIATED'
      }, {
        text: this.$t('complaints.statusNames.accepted'),
        value: 'ACCEPTED'
      }, {
        text: this.$t('complaints.statusNames.rejected'),
        value: 'REJECTED'
      }];
    }
  },
  data () {
    return {
      description: '',
      handled_by_id: null,
      complainedProducts: [],
      complainedServices: [],
      transportIsComplained: false,
      shipment_tracking_number: '',
      // helpers
      workers: [],
      // flags
      loadWorkersError: false
    };
  },
  mounted () {
    this.setComplainedProducts();
    this.setComplainedServices();
    this.loadAdditionalData();
  },
  methods: {
    filterByForWorkers (option, label, search) {
      return (option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    handleClose () {
      this.$bus.$emit('close-complaint-add-popup');
    },
    validateForm () {
      FormUtils.validate(this.$refs['complaint-add-form'], this.saveComplaint);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        errorKey: 'loadWorkersError',
        params: {
          return: 'get',
          where: {
            type: ['admin', 'worker']
          }
        }
      });
    },
    setComplainedProducts () {
      this.complainedProducts = [];

      for (let i = 0; i < this.products.length; i++) {
        let productData = this.products[i];
        let productToAdd = {
          is_complained: false,
          order_product_id: productData.id,
          order_quantity: productData.quantity,
          order_name: [productData.code, productData.name].filter(part => !!part).join(' - '),
          complaint_quantity: 0,
          complaint_description: '',
          complaint_status: 'RECEIVED',
          services: []
        };

        for (let j = 0; j < this.products[i].services.length; j++) {
          let serviceData = this.products[i].services[j];
          let serviceToAdd = {
            is_complained: false,
            order_service_id: serviceData.id,
            order_quantity: serviceData.quantity,
            order_name: [serviceData.code, serviceData.name].filter(part => !!part).join(' - '),
            complaint_quantity: 0,
            complaint_description: '',
            complaint_status: 'RECEIVED'
          };
          productToAdd.services.push(serviceToAdd);
        }

        this.complainedProducts.push(productToAdd);
      }
    },
    setComplainedServices () {
      this.complainedServices = [];

      for (let i = 0; i < this.services.length; i++) {
        let serviceData = this.services[i];
        let serviceToAdd = {
          is_complained: false,
          order_service_id: serviceData.id,
          order_quantity: serviceData.quantity,
          order_name: [serviceData.code, serviceData.name].filter(part => !!part).join(' - '),
          complaint_quantity: 0,
          complaint_description: '',
          complaint_status: 'RECEIVED'
        };
        this.complainedServices.push(serviceToAdd);
      }
    },
    prepareItemsToSend () {
      let items = [];

      for (let i = 0; i < this.complainedProducts.length; i++) {
        let product = this.complainedProducts[i];

        if (product.is_complained) {
          items.push({
            quantity: product.complaint_quantity,
            order_product_id: product.order_product_id,
            status: product.complaint_status,
            description: product.complaint_description
          });
        }

        for (let j = 0; j < product.services.length; j++) {
          let service = product.services[j];

          if (service.is_complained) {
            items.push({
              quantity: service.complaint_quantity,
              order_service_id: service.order_service_id,
              status: service.complaint_status,
              description: service.complaint_description
            });
          }
        }
      }

      for (let i = 0; i < this.complainedServices.length; i++) {
        let service = this.complainedServices[i];

        if (service.is_complained) {
          items.push({
            quantity: service.complaint_quantity,
            order_service_id: service.order_service_id,
            status: service.complaint_status,
            description: service.complaint_description
          });
        }
      }

      if (this.transportIsComplained) {
        items.push({
          shipment_tracking_number: this.shipment_tracking_number
        });
      }

      return items;
    },
    saveComplaint () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/complaints/store',
        formData: {
          order_id: this.orderID,
          handled_by_id: this.handled_by_id,
          description: this.description,
          items: this.prepareItemsToSend()
        },
        successTitle: this.$t('complaints.popup.addSuccessTitle'),
        successText: this.$t('complaints.popup.addSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('complaints.popup.addErrorText'),
        successAction: () => {
          this.$bus.$emit('complaints-refresh-list');
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    }
  }
}
</script>

<style lang="scss" scoped>
.product-item-complaint-params,
.service-item-complaint-params,
.product-item-service-item-complaint-params {
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 5px;
  padding: 10px;
}
</style>
